import {
  AfterContentInit,
  Component,
  computed,
  contentChildren,
  effect,
  inject,
  input,
  model,
  output,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { ControlChange } from '../../base/control-change-event';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxError } from '../form-group';
import { HxRadio } from './radio';
import { HX_RADIO_GROUP } from './radio-tokens';

/**
 * Radio group component that manages a collection of radio buttons. Implements ControlValueAccessor for Angular Forms
 * integration. Simplified to work like native radio inputs with minimal properties.
 *
 * @example
 *   ```html
 *   <!-- Two-way binding -->
 *   <hx-radio-group [(value)]="selectedOption">
 *     <hx-radio value="a">Option A</hx-radio>
 *     <hx-radio value="b">Option B</hx-radio>
 *   </hx-radio-group>
 *
 *   <!-- Reactive Forms -->
 *   <form [formGroup]="form">
 *     <hx-radio-group formControlName="choice">
 *       <hx-radio value="yes">Yes</hx-radio>
 *       <hx-radio value="no">No</hx-radio>
 *     </hx-radio-group>
 *   </form>
 *   ```;
 */
@Component({
  selector: 'hx-radio-group',
  templateUrl: './radio-group.html',
  styleUrls: ['./radio-group.css', '../../styles/form-control-tokens.css'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [HxError],
  providers: [
    {
      provide: HX_RADIO_GROUP,
      useExisting: HxRadioGroup,
    },
  ],
  host: {
    class: 'hx-radio-group',
    '[class]': 'className()',
    '[class.hx-radio-group--disabled]': 'disabledState()',
    '[class.hx-radio-group--readonly]': 'readonly()',
    '[attr.role]': '"radiogroup"',
    '[attr.aria-disabled]': 'disabledState() || null',
    '[attr.aria-readonly]': 'readonly() || null',
    '[attr.aria-invalid]': '_isInErrorState() || null',
    '[attr.aria-required]': 'required() || null',
    '[attr.aria-label]': 'ariaLabel() || null',
    '[attr.aria-labelledby]': 'ariaLabelledby() || null',
    '[attr.aria-describedby]': 'ariaDescribedby() || null',
    '(keydown)': '_onKeydown($event)',
    '(blur)': '_onBlur()',
  },
})
export class HxRadioGroup<T = any> extends HxBaseControl implements ControlValueAccessor, AfterContentInit {
  /**
   * Currently selected value. Supports two-way binding with [(value)] syntax.
   *
   * @example
   *   ```html
   *   <hx-radio-group [(value)]="selectedOption">
   *     <hx-radio value="a">Option A</hx-radio>
   *   </hx-radio-group>
   *   ```;
   *
   * @default null
   */
  readonly value = model<T | null>(null);

  /** Static counter for generating unique IDs */
  private static _nextId = 0;

  /**
   * Name attribute shared by all radio buttons in this group. Auto-generated if not provided.
   *
   * @default 'hx-radio-group-{uniqueId}'
   */
  readonly name = input<string>(`hx-radio-group-${HxRadioGroup._nextId++}`);

  /**
   * Pre-selected value for the radio group. Use this for one-way binding to set initial selection.
   *
   * @example
   *   ```html
   *   <hx-radio-group [selected]="'option2'">
   *     <hx-radio value="option1">Option 1</hx-radio>
   *     <hx-radio value="option2">Option 2</hx-radio>
   *   </hx-radio-group>
   *   ```;
   *
   * @default undefined
   */
  readonly selected = input<T | null | undefined>(undefined);

  /**
   * Custom CSS class name(s) to apply to the radio group.
   *
   * @example
   *   ```html
   *   <hx-radio-group className="custom-radio-group">
   *     <hx-radio value="option1">Option 1</hx-radio>
   *   </hx-radio-group>
   *   ```;
   *
   * @default ''
   */
  readonly className = input<string>('');

  /**
   * ARIA label for the radio group. Use when there's no visible label element.
   *
   * @example
   *   ```html
   *   <hx-radio-group aria-label="Choose size">
   *   ```;
   */
  override readonly ariaLabel = input<string | undefined>(undefined, { alias: 'aria-label' });

  /**
   * Custom error message displayed when required validation fails. Allows customization of the default "This field is
   * required" message.
   *
   * @example
   *   ```html
   *   <hx-radio-group
   *     [required]="true"
   *     requiredErrorMessage="Please select an option">
   *   ```;
   *
   * @default ''
   */
  readonly requiredErrorMessage = input<string>('');

  /**
   * Emits when the selected radio changes. The event includes the source radio and the new value.
   *
   * @example
   *   ```html
   *   <hx-radio-group (change)="onSelectionChange($event)">
   *   ```;
   */
  readonly change = output<ControlChange<T>>();

  /** Query all child radio buttons */
  protected readonly _radios = contentChildren(HxRadio, { descendants: true });

  /** Currently selected radio (computed) */
  protected readonly _selectedRadio = computed(() => {
    const currentValue = this.value();
    return this._radios().find((radio) => radio.value() === currentValue) || null;
  });

  /** NgControl for validation state */
  protected readonly _ngControl = inject(NgControl, { optional: true, self: true });

  /** Whether group is in error state */
  protected readonly _isInErrorState = computed(() => {
    const control = this._ngControl;
    return !!(control && control.invalid && (control.dirty || control.touched));
  });

  /** Get error message for display */
  protected readonly _errorMessage = computed(() => {
    const control = this._ngControl;
    if (!control) return '';

    // Check if we should show error
    const showError = control.invalid && (control.dirty || control.touched);
    if (!showError || !control.errors) return '';

    // Return appropriate error message
    if (control.errors['required']) {
      return this.requiredErrorMessage() || 'Please select an option';
    }

    return '';
  });

  /** ControlValueAccessor callbacks */
  protected override _onChange: (value: any) => void = () => {};
  protected override _onTouched: () => void = () => {};

  constructor() {
    super();
    // Register as ControlValueAccessor manually to avoid conflict with inject
    if (this._ngControl) {
      this._ngControl.valueAccessor = this;
    }

    // Initialize and react to selected property changes
    effect(() => {
      const initialSelected = this.selected();
      if (initialSelected !== undefined && this.value() !== initialSelected) {
        this.value.set(initialSelected);
      }
    });

    // Keep roving tabindex up to date on initial mount and whenever radios or value changes
    effect(() => {
      this._radios();
      this.value();
      this._updateTabIndex();
    });
  }

  ngAfterContentInit(): void {
    this._updateTabIndex();
  }

  /**
   * Update the selected value (called by child radios)
   *
   * @internal
   */
  _updateValue(newValue: T): void {
    if (this.readonly() || this.disabledState()) {
      return;
    }
    if (this.value() !== newValue) {
      this.value.set(newValue);
      this._markRadiosForCheck();

      const sourceRadio = this._radios().find((radio) => radio.value() === newValue);
      // Deselecting (newValue === null) has no matching radio; still emit so listeners see the cleared value.
      this._emitChangeEvent(sourceRadio ?? null, newValue);

      this._onChange(newValue);
      this._onTouched();
      this._updateTabIndex();
    }
  }

  /**
   * Update checked state on all child radios
   *
   * @internal
   * Note: Checked state is now computed in each HxRadio component
   */
  _markRadiosForCheck(): void {
    // No-op: checked state is automatically computed in child radios
  }

  /**
   * Emit change event
   *
   * @internal
   */
  private _emitChangeEvent(source: HxRadio<T> | null, value: T): void {
    this.change.emit(new ControlChange(source, value));
  }

  /**
   * Update tabindex for child radios: all non-disabled radios get tabindex 0 so users can navigate sequentially between
   * options with Tab.
   *
   * @internal
   */
  private _updateTabIndex(): void {
    const radios = this._radios();
    radios.forEach((radio) => {
      radio.tabindex.set(radio._isDisabled() ? -1 : 0);
    });
  }

  /**
   * Handle blur to mark as touched for Forms validation
   *
   * @internal
   */
  protected _onBlur(): void {
    this._onTouched();
  }

  /**
   * Handle keyboard navigation
   *
   * @internal
   */
  protected _onKeydown(event: KeyboardEvent): void {
    if (this.readonly() || this.disabledState()) {
      return;
    }
    const target = event.target as HTMLElement;
    const radios = this._radios();

    // Find which radio currently has focus
    let focusedIndex = -1;
    for (let i = 0; i < radios.length; i++) {
      const radio = radios[i];
      if (radio && radio._elementRef.nativeElement.contains(target)) {
        focusedIndex = i;
        break;
      }
    }

    if (focusedIndex === -1) {
      return;
    }

    let nextIndex = -1;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        nextIndex = this._getNextRadioIndex(focusedIndex);
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        nextIndex = this._getPreviousRadioIndex(focusedIndex);
        break;
      case 'Enter':
      case ' ':
      case 'Spacebar': // Older browsers.
        // Selection is explicit: moving highlight does not commit, committing a value needs a deliberate key
        // press (Enter or Space). Assessments drive conditional logic off these answers, so focus must never select by accident.
        event.preventDefault();
        this._selectRadioAt(focusedIndex);
        return;
      default:
        return;
    }

    if (nextIndex !== -1) {
      this._focusRadioAt(nextIndex);
    }
  }

  /**
   * Moves focus to a radio without changing the selected value.
   *
   * @internal
   */
  private _focusRadioAt(index: number): void {
    const radios = this._radios();
    const target = radios[index];
    if (!target) {
      return;
    }

    target.focus();
  }

  /**
   * Commits the radio at `index` as the group's value.
   *
   * @internal
   */
  private _selectRadioAt(index: number): void {
    const target = this._radios()[index];
    if (!target || target._isDisabled()) {
      return;
    }

    if (target.checked() || this.value() === target.value()) {
      this._updateValue(null as any);
    } else {
      this._updateValue(target.value());
    }
    target.focus();
  }

  /**
   * Get next radio index (skip disabled, wrap around)
   *
   * @internal
   */
  private _getNextRadioIndex(currentIndex: number): number {
    const radios = this._radios();
    if (radios.length === 0) {
      return -1;
    }

    let nextIndex = (currentIndex + 1) % radios.length;
    const startIndex = nextIndex;

    while (radios[nextIndex]?._isDisabled()) {
      nextIndex = (nextIndex + 1) % radios.length;
      if (nextIndex === startIndex) {
        return -1;
      } // All disabled
    }

    return nextIndex;
  }

  /**
   * Get previous radio index (skip disabled, wrap around)
   *
   * @internal
   */
  private _getPreviousRadioIndex(currentIndex: number): number {
    const radios = this._radios();
    if (radios.length === 0) {
      return -1;
    }

    let prevIndex = currentIndex - 1;
    if (prevIndex < 0) {
      prevIndex = radios.length - 1;
    }
    const startIndex = prevIndex;

    while (radios[prevIndex]?._isDisabled()) {
      prevIndex--;
      if (prevIndex < 0) {
        prevIndex = radios.length - 1;
      }
      if (prevIndex === startIndex) {
        return -1;
      } // All disabled
    }

    return prevIndex;
  }

  // ControlValueAccessor implementation

  override writeValue(value: T | null): void {
    this.value.set(value);
    this._markRadiosForCheck();
  }

  override registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  override registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  override setDisabledState(isDisabled: boolean): void {
    this._disabledFromCVA.set(isDisabled);
  }
}
